import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import type {
  AllergyIntoleranceType,
  Category,
  QuestionnaireResponse,
  ReactionEventSeverity,
} from "@curai/js-fhir-tools";

export interface ProcessedAllergy {
  allergenCode: string;
  allergenCodeSystem: string;
  allergenName?: string;
  allergyIntoleranceId?: string;
}

export interface FreetextAllergyResponse {
  allergenName: string;
}

export interface VisitIntakeAllergyData {
  processingSuccessful: boolean;
  nkaIndicated?: boolean;
  manualReconciliationRequired?: boolean;
  codifiedAllergies: ProcessedAllergy[];
  freetextAllergies: FreetextAllergyResponse[];
}

export interface VisitIntakeQuestionnaire {
  questionnaireResponse: QuestionnaireResponse;
}

export interface CodifiedMedication {
  drugCode: string;
  drugCodeSystem: string;
  drugName: string;
  addedToChartAt?: string;
}

export interface ProcessedMedication {
  name: string;
  dosage: string | null;
  drugForm: string | null;
  strength: string | null;
  frequencyValue: number | null;
  frequencyUnit: string | null;
  isPrn: boolean | null;
  frequencyDisplay: string | null;
  sourceId: string;
  codifiedDrugs: CodifiedMedication[];
}

export interface VisitIntakeMedicationData {
  btSpanId?: string;
  activeMedications: ProcessedMedication[];
  stoppedMedications: ProcessedMedication[];
  /** Set when user indicated medications were added manually that were not in the original suggestions */
  markedAsErroneousAt?: string | null;
}

export interface SnomedCode {
  code: string;
  display: string;
  system: string;
  addedToChartAt?: string | null;
}

export interface ProcessedCondition {
  term: string;
  snomedCodes: SnomedCode[];
  sourceId: string;
}

export interface VisitIntakeConditionData {
  btSpanId?: string;
  historicalConditions: ProcessedCondition[];
  rfeConditions: ProcessedCondition[];
  /** Set when user indicated conditions were added manually that were not in the original suggestions */
  markedAsErroneousAt?: string | null;
}

export type ExtractedAllergyCategory =
  | "food"
  | "biologic"
  | "environment"
  | "medication";

export interface ExtractedSymptom {
  symptom: string;
  symptomSourceId?: string | null;
}

export interface CodifiedAllergen {
  name?: string | null;
  brandName?: string | null;
  allergenId?: string | null;
  system?: string | null;
  addedToChartAt?: string | null;
}

export interface ExtractedAllergy {
  name?: string | null;
  type?: AllergyIntoleranceType | null;
  severity?: ReactionEventSeverity | null;
  category?: Category | null;
  sourceId?: string | null;
  symptoms: ExtractedSymptom[];
  codifiedAllergies: CodifiedAllergen[];
  amazonRxcui?: string | null;
  amazonRxcuiMatched?: boolean;
  amazonRxcuiAllergenId?: string | null;
}

export interface AmazonRxcuiApothraMismatch {
  amazonRxcui: string;
  allergenId: string;
  allergenName?: string | null;
  addedToChartAt?: string | null;
}

export interface AmazonAllergen {
  name?: string | null;
  rxcui?: string | null;
  allergenId?: string | null;
}

export interface AllergyExtractionResult {
  btSpanId?: string | null;
  currentAllergies: ExtractedAllergy[];
  amazonRxcuisApothraMismatch: AmazonRxcuiApothraMismatch[];
  amazonAllergens: AmazonAllergen[];
  /** Set when user indicated allergies were added manually that were not in the original suggestions */
  markedAsErroneousAt?: string | null;
  nkaMarkedAt?: string | null;
}

export interface GetVisitIntakeResponse {
  intakeQuestionnaire: VisitIntakeQuestionnaire;
  allergies?: VisitIntakeAllergyData;
  allergyExtraction?: AllergyExtractionResult;
  medications?: VisitIntakeMedicationData;
  conditions?: VisitIntakeConditionData;
}

export const useAmazonVisitIntake = ({
  patientId,
  amazonVisitId,
  enabled,
}: {
  patientId: string;
  amazonVisitId: string;
  enabled: boolean;
}) => {
  return useApi<GetVisitIntakeResponse>({
    queryOptions: {
      queryKey: QueryKeys.Patient.Chart.AmazonVisitIntake.DEFAULT({
        patientId,
        amazonVisitId,
      }),
      queryFn: async () => {
        const response = await axiosInstance.get(
          `amazon-clinic/visits/${amazonVisitId}/intake`,
        );
        return response.data;
      },
      enabled,
    },
  });
};
